<template>
  <div class="space-y-6">
    
    <!-- Statut de l'import -->
    <div class="text-center">
      <svg class="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h4 class="text-xl font-bold text-gray-900 mt-4">✅ Import terminé avec succès !</h4>
      <p class="text-gray-600 mt-2">Le schéma a été importé et intégré dans votre système.</p>
    </div>
    
    <!-- Informations du schéma créé -->
    <div v-if="schemaCreated" class="bg-blue-50 border border-blue-200 p-6 rounded-lg">
      <h5 class="font-bold text-blue-800 mb-3">🏗️ Schéma créé</h5>
      <div class="space-y-2 text-sm text-blue-700">
        <div class="flex justify-between">
          <span class="font-medium">UUID:</span>
          <code class="bg-blue-100 px-2 py-1 rounded text-xs">{{ schemaCreated.uuid }}</code>
        </div>
        <div class="flex justify-between">
          <span class="font-medium">Nom:</span>
          <span>{{ schemaCreated.appellation }}</span>
        </div>
        <div class="flex justify-between">
          <span class="font-medium">Sections assignées:</span>
          <span class="font-bold">{{ schemaCreated.sections_assigned }}</span>
        </div>
        <div v-if="schemaCreated.schema_translations_created > 0" class="flex justify-between">
          <span class="font-medium">Traductions spécifiques:</span>
          <span class="font-bold">{{ schemaCreated.schema_translations_created }}</span>
        </div>
      </div>
    </div>
    
    <!-- Résumé détaillé de l'import -->
    <div class="bg-green-50 border border-green-200 p-6 rounded-lg">
      <h5 class="font-bold text-green-800 mb-4">📊 Résumé détaillé de l'import</h5>
      
      <!-- Métriques principales -->
      <div class="grid grid-cols-2 gap-4 mb-6">
        <div class="bg-white p-4 rounded-lg border border-green-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-green-600 text-sm font-medium">Sections créées</p>
              <p class="text-2xl font-bold text-green-900">{{ importReport.sections_created }}</p>
            </div>
            <div class="bg-green-100 p-2 rounded-full">
              <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-4 rounded-lg border border-green-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-blue-600 text-sm font-medium">Sections mises à jour</p>
              <p class="text-2xl font-bold text-blue-900">{{ importReport.sections_updated }}</p>
            </div>
            <div class="bg-blue-100 p-2 rounded-full">
              <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-4 rounded-lg border border-green-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-purple-600 text-sm font-medium">Traductions créées</p>
              <p class="text-2xl font-bold text-purple-900">{{ importReport.translations_created }}</p>
            </div>
            <div class="bg-purple-100 p-2 rounded-full">
              <svg class="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </div>
          </div>
        </div>
        
        <div class="bg-white p-4 rounded-lg border border-green-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-orange-600 text-sm font-medium">Conflits résolus</p>
              <p class="text-2xl font-bold text-orange-900">{{ importReport.conflicts.length }}</p>
            </div>
            <div class="bg-orange-100 p-2 rounded-full">
              <svg class="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Statistiques additionnelles -->
      <div class="grid grid-cols-3 gap-4 text-sm">
        <div class="text-center">
          <div class="text-lg font-bold text-green-700">{{ importReport.translations_updated }}</div>
          <div class="text-green-600">Traductions mises à jour</div>
        </div>
        <div class="text-center">
          <div class="text-lg font-bold text-green-700">{{ importReport.conditions_created }}</div>
          <div class="text-green-600">Conditions créées</div>
        </div>
        <div class="text-center">
          <div class="text-lg font-bold text-green-700">{{ importReport.conditions_updated }}</div>
          <div class="text-green-600">Conditions mises à jour</div>
        </div>
      </div>
    </div>
    
    <!-- Détail des conflits résolus -->
    <div v-if="importReport.conflicts.length > 0" class="bg-orange-50 border border-orange-200 p-4 rounded-lg">
      <h5 class="font-bold text-orange-800 mb-3">🔧 Conflits résolus</h5>
      <div class="space-y-2">
        <div 
          v-for="conflict in importReport.conflicts" 
          :key="conflict.id"
          class="bg-white p-3 rounded border border-orange-100"
        >
          <div class="flex justify-between items-start">
            <div>
              <span class="font-medium text-gray-900">{{ conflict.section_id }}</span>
              <span class="text-sm text-gray-600 ml-2">
                ({{ getConflictTypeLabel(conflict.type) }})
              </span>
            </div>
            <span class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
              {{ getConflictResolutionText(conflict) }}
            </span>
          </div>
          <p class="text-sm text-gray-600 mt-1">{{ conflict.description }}</p>
        </div>
      </div>
    </div>
    
    <!-- Résumé textuel -->
    <div class="bg-gray-50 border border-gray-200 p-4 rounded-lg">
      <h5 class="font-bold text-gray-800 mb-3">📝 Résumé</h5>
      <p class="text-gray-700">{{ importSummary }}</p>
    </div>
    
    <!-- Actions suivantes suggérées -->
    <div class="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
      <h5 class="font-bold text-indigo-800 mb-3">🚀 Actions suggérées</h5>
      <ul class="space-y-2 text-sm text-indigo-700">
        <li class="flex items-start">
          <span class="text-indigo-500 mr-2">•</span>
          Vérifiez la configuration des nouvelles sections dans la section Administration
        </li>
        <li class="flex items-start">
          <span class="text-indigo-500 mr-2">•</span>
          Testez les traductions importées dans l'interface utilisateur
        </li>
        <li v-if="schemaCreated && schemaCreated.schema_translations_created > 0" class="flex items-start">
          <span class="text-indigo-500 mr-2">•</span>
          Consultez les traductions spécifiques au schéma créées
        </li>
        <li class="flex items-start">
          <span class="text-indigo-500 mr-2">•</span>
          Sauvegardez la configuration avant d'effectuer d'autres imports
        </li>
      </ul>
    </div>
    
    <!-- Statistiques de performance -->
    <div class="bg-gray-100 border border-gray-300 p-4 rounded-lg">
      <h5 class="font-bold text-gray-700 mb-3">⚡ Statistiques de performance</h5>
      <div class="grid grid-cols-2 gap-4 text-sm text-gray-600">
        <div>
          <span class="font-medium">Durée totale:</span>
          <span class="ml-2">{{ processingTime }}</span>
        </div>
        <div>
          <span class="font-medium">Éléments traités:</span>
          <span class="ml-2">{{ totalItemsProcessed }}</span>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ImportReport, ImportConflict } from '@/api/types/core/schemaJson';
import { SchemaImportUtils } from '../../../utils/schemaImportUtils';

// Props
const props = defineProps<{
  importReport: ImportReport;
  startTime?: Date;
}>();

// Schéma créé (si présent dans le rapport étendu)
const schemaCreated = computed(() => {
  const report = props.importReport as ImportReport & { 
    schema_created?: { 
      uuid: string; 
      appellation: string; 
      sections_assigned: number; 
      schema_translations_created: number 
    } 
  };
  return report.schema_created;
});

// Résumé textuel de l'import
const importSummary = computed((): string => {
  return SchemaImportUtils.generateImportSummary(props.importReport);
});

// Calcul du temps de traitement
const processingTime = computed((): string => {
  if (!props.startTime) return 'Non disponible';
  
  const endTime = new Date();
  const duration = endTime.getTime() - props.startTime.getTime();
  const seconds = Math.round(duration / 1000);
  
  if (seconds < 60) {
    return `${seconds} seconde${seconds > 1 ? 's' : ''}`;
  } else {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
});

// Total des éléments traités
const totalItemsProcessed = computed((): number => {
  return (
    props.importReport.sections_created +
    props.importReport.sections_updated +
    props.importReport.translations_created +
    props.importReport.translations_updated +
    props.importReport.conditions_created +
    props.importReport.conditions_updated
  );
});

// Utilitaires
const getConflictTypeLabel = (type: string): string => {
  return SchemaImportUtils.getConflictTypeLabel(type);
};

const getConflictResolutionText = (conflict: ImportConflict): string => {
  return SchemaImportUtils.getConflictResolutionText(conflict);
};
</script> 